import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useProveedores } from '../hooks/useProveedores';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { VerInstructivo } from '../components/VerInstructivo';
import { FieldHint } from '../components/FieldHint';
import { api, extractErrorMessage, getAccessToken } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { montoAPagar } from '../lib/montoAPagar';
import { avisoRetencionIibb, jurisdiccionInicialLinea } from '../lib/iibb';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useCamposObligatorios, faltaSi } from '../hooks/useCamposObligatorios';
import { Obligatorio, LeyendaObligatorio } from '../components/Obligatorio';

interface CertificadoRetencion { id: number; numero: number; jurisdiccionId: number; montoRetenido: string; estado: 'emitido' | 'anulado'; }
interface RetencionAplicada { id: number; jurisdiccionId: number; tipo: string; baseImponible: number; alicuotaAplicada: number; monto: number; origen: 'padron' | 'default' | 'manual'; certificadoId: number; certificadoNumero: number; }
interface Pago {
  id: number;
  facturaId?: number;
  vencimientoId?: number;
  proveedorId?: number;
  fecha: string;
  monto: string;
  medioPago: string;
  referencia?: string;
  montoRetenciones?: string;
  certificados?: CertificadoRetencion[];
}
interface FacturaPend { id: number; numeroFactura?: string; fechaEmision: string; fechaVencimiento: string; montoTotal: string; montoPagado: string; estado: string; descuentoProntoPagoPorcentaje?: string; fechaLimiteProntoPago?: string | null; anticipoAplicado?: string; notasCreditoAplicadas?: string; tipo?: string; }
interface Jurisdiccion { id: number; nombre: string; codigo: string; activo: boolean; }
interface RetencionLinea { jurisdiccionId: string; baseImponible: string; alicuotaManual: string; }

const pagoVacio = () => ({ proveedorId: '', proveedorNombre: '', monto: '', medioPago: 'transferencia', referencia: '', fecha: new Date().toISOString().slice(0, 10) });

export function PagosPage() {
  const { data, loading, refetch } = useApi<Pago[]>('/api/v3/pagos');
  const { data: proveedores } = useProveedores();
  const { data: jurisdicciones, error: errorJurisdicciones } = useApi<Jurisdiccion[]>('/api/v3/impositivo/jurisdicciones');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filtered = (data ?? []).filter(row => Object.entries(filters).every(([key, val]) => !val || String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase())));
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [provSearch, setProvSearch] = useState(''); const [showProv, setShowProv] = useState(false);
  const [facturasPend, setFacturasPend] = useState<FacturaPend[]>([]);
  const [selectedFacturas, setSelectedFacturas] = useState<number[]>([]);
  const [form, setForm] = useState(pagoVacio);
  const [retencionLineas, setRetencionLineas] = useState<RetencionLinea[]>([]);
  const [aplicarDescuento, setAplicarDescuento] = useState(false);
  const [resultadoPago, setResultadoPago] = useState<{ pago: any; retencionesPago: RetencionAplicada[] } | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pagoToDelete, setPagoToDelete] = useState<Pago | null>(null);
  // Saldo a favor del proveedor (notas de crédito con crédito disponible): se aplica a una factura antes de pagar
  const [saldoAFavor, setSaldoAFavor] = useState(0);
  const [aplicandoCredito, setAplicandoCredito] = useState(false);
  const toast = useToast();
  const { can } = useAuth();

  // Cerrar el pago nuevo lo descarta; guardarlo también cierra (sin preguntar).
  const cerrarPago = () => {
    setModalOpen(false); setSelectedFacturas([]); setFacturasPend([]); setRetencionLineas([]); setAplicarDescuento(false);
    setForm(pagoVacio()); setProvSearch(''); setSaldoAFavor(0);
  };
  const cierrePago = useCierreConfirmado(useHayCambios({ form, provSearch, selectedFacturas, retencionLineas, aplicarDescuento }, modalOpen), cerrarPago);
  // Obligatorios del pago (backend: CreatePagoDto): proveedor, fecha y monto (mayor a 0)
  const obligatorios = useCamposObligatorios(modalOpen);

  const filteredProvs = (proveedores??[]).filter(p=>p.nombre.toLowerCase().includes(provSearch.toLowerCase())||String(p.id).includes(provSearch));

  // Retenciones only apply to a single-factura payment in v1 — the reconciliation
  // invariant (monto + Σ retenciones) is defined against ONE factura; splitting a
  // combined retención across a multi-factura payment is an unresolved distribution
  // problem, so the section is only shown/sent when exactly one factura is selected.
  const puedeRetener = selectedFacturas.length === 1;
  const facturaUnica = puedeRetener ? facturasPend.find(f => f.id === selectedFacturas[0]) : undefined;

  const cargarFacturasProveedor = async (proveedorId: number) => {
    try {
      const r = await api.get<FacturaPend[]>(`/api/v3/facturas?proveedor_id=${proveedorId}`);
      // Saldo = a pagar (total − anticipo − notas de crédito aplicadas) − pagado
      const pendientes = (r??[]).filter(f => f.estado !== 'pagada' && f.estado !== 'anulada' && f.estado !== 'cancelada' && montoAPagar(f) > Number(f.montoPagado||0));
      setFacturasPend(pendientes);
      return pendientes;
    } catch { setFacturasPend([]); return []; }
  };
  const cargarSaldoAFavor = async (proveedorId: number) => {
    try {
      const r = await api.get<{ saldoAFavor?: number }>(`/api/v3/resumen?proveedor_id=${proveedorId}`);
      setSaldoAFavor(Number(r?.saldoAFavor ?? 0));
    } catch { setSaldoAFavor(0); }
  };

  const selectProveedor = async (p: any) => {
    setForm({...form, proveedorId: String(p.id), proveedorNombre: p.nombre});
    setProvSearch(p.nombre); setShowProv(false); setSelectedFacturas([]); setRetencionLineas([]); setAplicarDescuento(false);
    setSaldoAFavor(0);
    await Promise.all([cargarFacturasProveedor(p.id), cargarSaldoAFavor(p.id)]);
  };

  // Aplica el saldo a favor a la factura elegida (hasta cubrir su saldo) y recalcula lo que queda por pagar
  const aplicarSaldoAFavor = async () => {
    if (!facturaUnica || !form.proveedorId) return;
    setAplicandoCredito(true);
    try {
      const r = await api.post<{ montoAplicado: number }>(`/api/v3/facturas/${facturaUnica.id}/aplicar-credito`, {});
      toast.addToast(`Saldo a favor aplicado: $${Number(r?.montoAplicado ?? 0).toLocaleString('es-AR')}`, 'success');
      const proveedorId = parseInt(form.proveedorId);
      const [pendientes] = await Promise.all([cargarFacturasProveedor(proveedorId), cargarSaldoAFavor(proveedorId)]);
      const actualizada = pendientes.find(f => f.id === facturaUnica.id);
      setAplicarDescuento(false);
      if (actualizada) {
        setForm(prev => ({ ...prev, monto: String(montoAPagar(actualizada) - Number(actualizada.montoPagado || 0)) }));
      } else {
        // Quedó saldada con el crédito: no hay nada que pagar
        setSelectedFacturas([]); setRetencionLineas([]);
        setForm(prev => ({ ...prev, monto: '' }));
      }
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
    finally { setAplicandoCredito(false); }
  };

  const toggleFactura = (id: number) => {
    const next = selectedFacturas.includes(id) ? selectedFacturas.filter(fid => fid !== id) : [...selectedFacturas, id];
    setSelectedFacturas(next);
    setAplicarDescuento(false);
    if (next.length !== 1) setRetencionLineas([]);
    const total = facturasPend.filter(f => next.includes(f.id)).reduce((sum, f) => sum + montoAPagar(f) - Number(f.montoPagado||0), 0);
    setForm({...form, monto: String(total)});
  };

  const toggleAplicarDescuento = () => {
    const next = !aplicarDescuento;
    setAplicarDescuento(next);
    if (!facturaUnica) return;
    const montoPagado = Number(facturaUnica.montoPagado || 0);
    if (next) {
      const pct = Number(facturaUnica.descuentoProntoPagoPorcentaje || 0);
      const montoTotalConDescuento = montoAPagar(facturaUnica) * (1 - pct / 100);
      setForm({...form, monto: String(Math.max(0, montoTotalConDescuento - montoPagado))});
    } else {
      const saldo = montoAPagar(facturaUnica) - montoPagado;
      setForm({...form, monto: String(saldo)});
    }
  };

  // Datos de IIBB del proveedor, solo como ayuda: la línea nueva arranca con su jurisdicción sede y, si figura
  // como exento, se avisa. No cambian el cálculo de la retención.
  const proveedorSel = (proveedores ?? []).find((p: any) => String(p.id) === form.proveedorId);
  const avisoIibb = avisoRetencionIibb(proveedorSel?.iibbCondicion);
  const addRetencionLinea = () => setRetencionLineas([...retencionLineas, {
    jurisdiccionId: jurisdiccionInicialLinea(proveedorSel?.jurisdiccionSedeId, jurisdicciones ?? [], retencionLineas.map(l => l.jurisdiccionId)),
    baseImponible: '', alicuotaManual: '',
  }]);
  const removeRetencionLinea = (idx: number) => setRetencionLineas(retencionLineas.filter((_, i) => i !== idx));
  const updateRetencionLinea = (idx: number, field: keyof RetencionLinea, value: string) =>
    setRetencionLineas(retencionLineas.map((l, i) => i === idx ? { ...l, [field]: value } : l));

  const estimadoRetenido = retencionLineas.reduce((sum, l) => {
    const base = parseFloat(l.baseImponible) || 0;
    const alic = parseFloat(l.alicuotaManual);
    return sum + (isNaN(alic) ? 0 : base * alic / 100);
  }, 0);

  const descargarCertificado = async (certificadoId: number, numero: number) => {
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`/api/v3/impositivo/certificados/${certificadoId}/pdf`, { headers, credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || 'Error al descargar el certificado');
      }
      const blob = await res.blob();
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = `certificado-retencion-${numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(urlObj);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  const handleCreate = async () => {
    if (!obligatorios.validar()) return;
    setSubmitting(true);
    try {
      const retencionesPago = puedeRetener
        ? retencionLineas
            .filter(l => l.jurisdiccionId && l.baseImponible)
            .map(l => ({
              jurisdiccionId: parseInt(l.jurisdiccionId),
              baseImponible: parseFloat(l.baseImponible),
              alicuotaManual: l.alicuotaManual ? parseFloat(l.alicuotaManual) : undefined,
            }))
        : [];

      const payload: Record<string, any> = {
        proveedorId: parseInt(form.proveedorId),
        monto: parseFloat(form.monto),
        medioPago: form.medioPago,
        referencia: form.referencia,
        fecha: form.fecha,
      };
      // Only route through the single-facturaId path when there ARE
      // retenciones to withhold or a pronto-pago discount to apply —
      // zero-retención, zero-descuento payments keep using the existing
      // facturaIds (multi-select) path unchanged, byte-identically.
      if (puedeRetener && (retencionesPago.length > 0 || aplicarDescuento)) {
        payload.facturaId = selectedFacturas[0];
        if (retencionesPago.length > 0) payload.retencionesPago = retencionesPago;
        if (aplicarDescuento) payload.aplicarDescuentoProntoPago = true;
      } else {
        payload.facturaIds = selectedFacturas;
      }

      const result = await api.post<any>('/api/v3/pagos', payload);

      if (result?.retencionesPago?.length > 0) {
        setResultadoPago({ pago: result, retencionesPago: result.retencionesPago });
        toast.addToast('Pago registrado — certificados de retención generados', 'success');
      } else {
        toast.addToast('Pago registrado', 'success');
      }
      cerrarPago();
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!pagoToDelete) return;
    try {
      await api.delete(`/api/v3/pagos/${pagoToDelete.id}`);
      toast.addToast('Pago eliminado — saldo devuelto a la factura', 'success');
      setDeleteConfirmOpen(false);
      setPagoToDelete(null);
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  const columns = [
    { key: 'id', header: 'ID', help: 'Número de pago' },
    { key: 'proveedorId', header: 'Proveedor', render: (row: Pago) => row.proveedorId ?? '—', help: 'Proveedor al que se pagó' },
    { key: 'fecha', header: 'Fecha', render: (row: Pago) => new Date(row.fecha).toLocaleDateString('es-AR'), help: 'Fecha del pago' },
    { key: 'monto', header: 'Monto', render: (row: Pago) => `$${Number(row.monto).toLocaleString('es-AR')}`, help: 'Monto transferido (neto de retenciones)' },
    { key: 'montoRetenciones', header: 'Retenido', render: (row: Pago) => Number(row.montoRetenciones||0) > 0 ? `$${Number(row.montoRetenciones).toLocaleString('es-AR')}` : '—', help: 'Total retenido IIBB en este pago' },
    { key: 'medioPago', header: 'Medio', help: 'Medio de pago utilizado' },
    { key: 'referencia', header: 'Referencia', help: 'Referencia del pago' },
    { key: 'certificados', header: 'Certificados', render: (row: Pago) => (
      row.certificados && row.certificados.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {row.certificados.map(c => (
            <button
              key={c.id}
              onClick={(e) => { e.stopPropagation(); descargarCertificado(c.id, c.numero); }}
              disabled={!can('retenciones', 'ver')}
              title={!can('retenciones', 'ver') ? 'No tenés permiso para realizar esta acción' : (c.estado === 'anulado' ? 'Certificado anulado — descarga disponible para auditoría' : 'Descargar certificado PDF')}
              className={`text-xs underline disabled:opacity-50 disabled:cursor-not-allowed ${c.estado === 'anulado' ? 'text-red-500' : 'text-brand-600 hover:text-brand-800'}`}
            >
              #{c.numero}{c.estado === 'anulado' ? ' (anulado)' : ''}
            </button>
          ))}
        </div>
      ) : <span className="text-slate-300">—</span>
    ), help: 'Certificados de retención emitidos para este pago' },
    { key: 'actions', header: '', render: (row: Pago) => (
      <button
        onClick={() => { setPagoToDelete(row); setDeleteConfirmOpen(true); }}
        disabled={!can('pagos', 'eliminar')}
        className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        title={!can('pagos', 'eliminar') ? 'No tenés permiso para realizar esta acción' : 'Eliminar pago'}
      >
        Eliminar
      </button>
    ), help: 'Eliminar pago y devolver saldo a la factura' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-800 inline">Pagos</h1><HelpTip tips={['Buscá el proveedor, después tildá las facturas que querés pagar','El monto se acumula automáticamente al seleccionar facturas','Si pagás menos del total, se aplica de la factura más vieja a la más nueva','Las facturas cubiertas pasan a estado pagada, las parciales a parcial','Si seleccionás una sola factura podés cargar retenciones IIBB por jurisdicción']} /><VerInstructivo seccion="pagos" /><p className="text-slate-500 text-sm mt-1">Registro de pagos a proveedores</p></div>
        <Button onClick={() => { setAplicarDescuento(false); setModalOpen(true); }} disabled={!can('pagos', 'crear')} title={!can('pagos', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}>+ Registrar Pago</Button>
      </div>
      <DataTable columns={columns} data={filtered} loading={loading}
        filters={filters} onFilterChange={(k,v)=>setFilters(f=>v?{...f,[k]:v}:Object.fromEntries(Object.entries(f).filter(([key])=>key!==k)))} />
      <Modal open={modalOpen} cierre={cierrePago} title="Registrar Pago" size="lg">
        <div {...obligatorios.contenedor} className="space-y-4">
          <LeyendaObligatorio />
          <div className="relative"><label className="block text-sm font-medium text-slate-700 mb-1">Proveedor<Obligatorio /> <FieldHint text="Buscá el proveedor por nombre. Al seleccionarlo aparecen sus facturas pendientes de pago" /></label>
            <input aria-label="Proveedor" {...faltaSi(!form.proveedorId)} value={provSearch||form.proveedorNombre} onChange={e=>{setProvSearch(e.target.value);setShowProv(true)}} onFocus={()=>setShowProv(true)} onBlur={()=>setTimeout(()=>setShowProv(false),200)} placeholder="Buscar proveedor..." className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
            {showProv && filteredProvs.length>0 && (<div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">{filteredProvs.map(p=>(<div key={p.id} onClick={()=>selectProveedor(p)} className="px-3 py-1.5 text-sm hover:bg-brand-50 cursor-pointer">{p.nombre} (ID:{p.id})</div>))}</div>)}
          </div>
          {facturasPend.length > 0 && (<>
            <h3 className="text-sm font-semibold text-slate-700 pt-2 border-t">Facturas pendientes — seleccioná las que querés pagar</h3>
            <div className="max-h-48 overflow-y-auto border rounded-lg">
              {facturasPend.map(f => (() => { const saldo = montoAPagar(f) - Number(f.montoPagado||0); return (
                <label key={f.id} className={`flex items-center gap-3 px-3 py-2 border-b text-sm cursor-pointer hover:bg-slate-50 ${selectedFacturas.includes(f.id) ? 'bg-brand-50' : ''}`}>
                  <input type="checkbox" checked={selectedFacturas.includes(f.id)} onChange={()=>toggleFactura(f.id)} className="rounded" />
                  <span className="flex-1"><span className="font-medium">{f.numeroFactura || `#${f.id}`}</span> <span className="text-slate-400">— {new Date(f.fechaVencimiento).toLocaleDateString('es-AR')}</span></span>
                  <span className={`font-medium ${saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>${saldo.toLocaleString('es-AR')}</span>
                </label>
              )})())}
            </div>
          </>)}
          {saldoAFavor > 0 && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm space-y-2">
              <p className="text-emerald-800">
                Saldo a favor del proveedor: <span className="font-semibold">${saldoAFavor.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span> (notas de crédito sin aplicar)
              </p>
              {facturaUnica ? (
                <Button onClick={aplicarSaldoAFavor} loading={aplicandoCredito} disabled={!can('pagos', 'crear')} title={!can('pagos', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}>
                  Aplicar saldo a favor a {facturaUnica.numeroFactura || `#${facturaUnica.id}`}
                </Button>
              ) : (
                <p className="text-xs text-emerald-700">Tildá una sola factura para aplicarle el saldo a favor antes de registrar el pago.</p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fecha<Obligatorio /> <FieldHint text="Fecha en que se realiza el pago" /></label>
            <input type="date" aria-label="Fecha" required value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
          {puedeRetener && facturaUnica?.descuentoProntoPagoPorcentaje && (() => {
            const pct = Number(facturaUnica.descuentoProntoPagoPorcentaje);
            const montoConDescuento = montoAPagar(facturaUnica) * (1 - pct / 100);
            return (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm space-y-2">
                <p className="text-emerald-800">
                  {pct}% si paga hasta el {new Date(facturaUnica.fechaLimiteProntoPago ?? facturaUnica.fechaVencimiento).toLocaleDateString('es-AR', { timeZone: 'UTC' })} → ${montoConDescuento.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={aplicarDescuento} onChange={toggleAplicarDescuento} className="rounded" />
                  <span>Aplicar descuento por pronto pago</span>
                </label>
              </div>
            );
          })()}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Monto<Obligatorio /> <FieldHint text="Monto a pagar. Se auto-completa al tildar facturas. Podés editarlo si pagás un monto distinto" /></label>
            <input type="number" aria-label="Monto" {...faltaSi(!(parseFloat(form.monto) > 0))} value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Medio de Pago <FieldHint text="Forma de pago: transferencia bancaria, cheque o efectivo" /></label>
            <select value={form.medioPago} onChange={(e) => setForm({ ...form, medioPago: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
              <option value="transferencia">Transferencia</option>
              <option value="cheque">Cheque</option>
              <option value="efectivo">Efectivo</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Referencia</label>
            <input aria-label="Referencia" value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
          {puedeRetener && (
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">Retenciones IIBB{retencionLineas.length>0?` (${retencionLineas.length})`:''} <FieldHint text="Opcional. Una línea por jurisdicción — la alícuota se resuelve por padrón/default salvo que la cargues manualmente" /></h3>
                <button type="button" onClick={addRetencionLinea} disabled={!can('pagos','crear')} className="text-xs text-brand-600 hover:text-brand-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed">+ Agregar jurisdicción</button>
              </div>
              {errorJurisdicciones && <p className="text-xs text-amber-700 mb-2">No se pudo cargar la lista de jurisdicciones (hace falta el permiso de Impositivo): las retenciones de IIBB no se pueden elegir.</p>}
              {avisoIibb && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">{avisoIibb}</p>}
              {retencionLineas.length > 0 && (
                <div className="overflow-x-auto">
                  <div className="hidden md:grid md:grid-cols-12 md:gap-1 text-[10px] text-slate-400 px-1 md:min-w-[520px]">
                    <span className="col-span-5">Jurisdicción</span><span className="text-right col-span-3">Base Imponible</span><span className="text-right col-span-3">Alícuota Manual %</span><span></span>
                  </div>
                  <div className="space-y-2 md:min-w-[520px]">
                    {retencionLineas.map((linea, idx) => (
                      <div key={idx} className="relative rounded-lg border border-slate-200 bg-white p-3 md:border-0 md:bg-transparent md:p-0 md:rounded-none">
                        {/* Desktop/tablet row (md and up) */}
                        <div className="hidden md:grid md:grid-cols-12 md:gap-1 items-center">
                          <select value={linea.jurisdiccionId} onChange={e=>updateRetencionLinea(idx,'jurisdiccionId',e.target.value)} className="col-span-5 px-2 py-1 border rounded text-xs">
                            <option value="">Seleccionar...</option>
                            {(jurisdicciones??[]).map(j=>(<option key={j.id} value={j.id}>{j.nombre}</option>))}
                          </select>
                          <input type="number" value={linea.baseImponible} onChange={e=>updateRetencionLinea(idx,'baseImponible',e.target.value)} className="col-span-3 px-2 py-1 border rounded text-xs text-right" />
                          <input type="number" value={linea.alicuotaManual} onChange={e=>updateRetencionLinea(idx,'alicuotaManual',e.target.value)} placeholder="auto" className="col-span-3 px-2 py-1 border rounded text-xs text-right" />
                          <button onClick={()=>removeRetencionLinea(idx)} aria-label="Eliminar línea de retención" className="col-span-1 text-red-500 text-xs self-center">✕</button>
                        </div>
                        {/* Mobile card (below md) */}
                        <div className="md:hidden space-y-1">
                          <button onClick={()=>removeRetencionLinea(idx)} aria-label="Eliminar línea de retención" className="absolute top-1 right-1 text-red-500 min-w-[2.5rem] min-h-[2.5rem] flex items-center justify-center">✕</button>
                          <div className="pr-12">
                            <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Jurisdicción</label>
                            <select value={linea.jurisdiccionId} onChange={e=>updateRetencionLinea(idx,'jurisdiccionId',e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm">
                              <option value="">Seleccionar...</option>
                              {(jurisdicciones??[]).map(j=>(<option key={j.id} value={j.id}>{j.nombre}</option>))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Base Imponible</label>
                            <input type="number" value={linea.baseImponible} onChange={e=>updateRetencionLinea(idx,'baseImponible',e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Alícuota Manual %</label>
                            <input type="number" value={linea.alicuotaManual} onChange={e=>updateRetencionLinea(idx,'alicuotaManual',e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                <span>Transferís: <strong>${(parseFloat(form.monto)||0).toLocaleString('es-AR')}</strong></span>
                <span>Retenés{estimadoRetenido>0?' (estimado)':''}: <strong>{estimadoRetenido>0?`$${estimadoRetenido.toLocaleString('es-AR')}`:'a calcular al confirmar'}</strong></span>
                <span>Saldás: <strong>${((parseFloat(form.monto)||0)+estimadoRetenido).toLocaleString('es-AR')}{estimadoRetenido===0 && retencionLineas.length>0 ? ' o más' : ''}</strong></span>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button onClick={cierrePago.cerrar} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancelar</button>
            <Button onClick={handleCreate} loading={submitting} disabled={!can('pagos', 'crear')} title={!can('pagos', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}>{submitting ? 'Guardando...' : 'Registrar'}</Button>
          </div>
        </div>
      </Modal>
      <Modal open={!!resultadoPago} onClose={() => setResultadoPago(null)} title="Pago Registrado — Retenciones" size="md">
        {resultadoPago && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg text-center">
              <div><div className="text-slate-400 text-xs uppercase tracking-wide">Transferiste</div><div className="font-bold text-base">${Number(resultadoPago.pago.monto).toLocaleString('es-AR')}</div></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wide">Retuviste</div><div className="font-bold text-base text-amber-600">${Number(resultadoPago.pago.montoRetenciones||0).toLocaleString('es-AR')}</div></div>
              <div><div className="text-slate-400 text-xs uppercase tracking-wide">Saldaste</div><div className="font-bold text-base text-green-600">${(Number(resultadoPago.pago.monto)+Number(resultadoPago.pago.montoRetenciones||0)).toLocaleString('es-AR')}</div></div>
            </div>
            <div className="space-y-2">
              {resultadoPago.retencionesPago.map(r => {
                const jurisdiccionNombre = (jurisdicciones??[]).find(j=>j.id===r.jurisdiccionId)?.nombre ?? `#${r.jurisdiccionId}`;
                return (
                  <div key={r.id} className="flex items-center justify-between px-3 py-2 border rounded-lg text-sm">
                    <div><span className="font-medium">{jurisdiccionNombre}</span> <span className="text-slate-400">— {Number(r.alicuotaAplicada).toLocaleString('es-AR')}%</span></div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">${Number(r.monto).toLocaleString('es-AR')}</span>
                      <button
                        onClick={()=>descargarCertificado(r.certificadoId, r.certificadoNumero)}
                        disabled={!can('retenciones','ver')}
                        title={!can('retenciones','ver') ? 'No tenés permiso para realizar esta acción' : undefined}
                        className="text-brand-600 hover:text-brand-800 text-xs underline disabled:opacity-50 disabled:cursor-not-allowed"
                      >Certificado #{r.certificadoNumero}</button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={()=>setResultadoPago(null)}>Cerrar</Button>
            </div>
          </div>
        )}
      </Modal>
      <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} title="Confirmar eliminación" size="sm">
        <div className="space-y-4">
          <p className="text-slate-700">¿Desea devolver el saldo pendiente a la factura?</p>
          {pagoToDelete && (
            <p className="text-sm text-slate-500">
              Pago #{pagoToDelete.id} — ${Number(pagoToDelete.monto).toLocaleString('es-AR')} del {new Date(pagoToDelete.fecha).toLocaleDateString('es-AR')}
              {Number(pagoToDelete.montoRetenciones||0) > 0 && (<> (retenciones ${Number(pagoToDelete.montoRetenciones).toLocaleString('es-AR')} también se revierten y sus certificados se anulan)</>)}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setDeleteConfirmOpen(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancelar</button>
            <button
              onClick={handleDelete}
              disabled={!can('pagos', 'eliminar')}
              title={!can('pagos', 'eliminar') ? 'No tenés permiso para realizar esta acción' : undefined}
              className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >Confirmar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
